import { useEffect } from "react";

import PropTypes from "prop-types";

import SectionTitle from "@/directory/SectionTitle";
import PrincipalSchoolProfile from "@/principal/PrincipalSchoolProfile";
import TeacherClassroomProfile from "@/teacher/TeacherClassroomProfile";
import TeacherSchoolProfile from "@/teacher/TeacherSchoolProfile";
import ResetAccountPassword from "@/users/ResetAccountPassword";
import UserProfile from "@/users/UserProfile";
import ClassesTable from "../ClassesTable";
import AdditionalInformation from "../AdditionalInformation";
import {
  userProfileTitleCss,
  classContainerCss,
  preferenceContainerCss,
  classSectionTitleCss,
  schoolContainerCss,
} from "./ProfileDetails.style";

const propTypes = {
  user: PropTypes.shape({
    roles: PropTypes.arrayOf(PropTypes.string).isRequired,
    schools: PropTypes.array.isRequired,
    _editable: PropTypes.shape({
      username: PropTypes.bool,
      password: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  teacher: PropTypes.object,
  onChangeUser: PropTypes.func.isRequired,
  onChangeTeacher: PropTypes.func.isRequired,
};

const ProfileDetails = ({ user, teacher, onChangeUser, onChangeTeacher }) => {
  const renderUserProfileSection = () => {
    const hiddenFields = ["email", "is_active"];
    if (!user._editable.username) hiddenFields.push("username");
    return (
      <section id="user-profile">
        <SectionTitle __css={userProfileTitleCss}>User profile</SectionTitle>
        <UserProfile
          user={user}
          teacher={teacher}
          onChangeUser={onChangeUser}
          onChangeTeacher={onChangeTeacher}
          hiddenFields={hiddenFields}
        />
      </section>
    );
  };

  const renderResetPasswordSection = () => (
    <section id="user-password">
      <SectionTitle separator>User password</SectionTitle>
      <ResetAccountPassword user={user} />
    </section>
  );

  const renderTeacherSchoolSection = () => {
    const isPrincipal = user.roles.includes("principal");

    return (
      <section id="school-profile">
        <SectionTitle separator>School profile</SectionTitle>

        <div css={schoolContainerCss}>
          <TeacherSchoolProfile user={user} teacher={teacher} />
          {isPrincipal && <PrincipalSchoolProfile user={user} />}
        </div>
      </section>
    );
  };

  const renderClassSection = () => (
    <section id="classes">
      <SectionTitle separator __css={classSectionTitleCss}>
        Classes
      </SectionTitle>
      <div css={classContainerCss}>
        <ClassesTable />
      </div>
    </section>
  );

  const renderContentPreferenceSection = () => (
    <section id="content-preferences">
      <SectionTitle
        separator
        description="Preferences help customize content recommendations (but you’ll still have access to all licensed content)."
      >
        Content preferences
      </SectionTitle>
      <div css={preferenceContainerCss}>
        <TeacherClassroomProfile
          teacher={teacher}
          onTeacherUpdated={onChangeTeacher}
        />
      </div>
    </section>
  );

  const renderAdditionalInfoSection = () => (
    <section id="additional-info">
      <SectionTitle separator>Additional info</SectionTitle>
      <AdditionalInformation user={user} />
    </section>
  );

  useEffect(() => {
    let hash = window.location.hash;
    hash = hash.replace("#", "");

    if (hash) {
      setTimeout(() => {
        // Will prevent white screens errors in case of a deprecated or wrong hash ex. newsela.com/account/settings/#really-bad-hash
        try {
          const anchor = document.getElementById(hash);
          if (anchor) {
            anchor.scrollIntoView({ behavior: "smooth" });
          } else {
            throw new Error(
              `Anchor for hash ${hash} not found at settings page.`,
            );
          }
        } catch (error) {
          console.error(error);
        }
      }, 500);
    }
  });

  return (
    <>
      {renderUserProfileSection()}
      {user._editable.password && renderResetPasswordSection()}
      {user.roles.includes("teacher") &&
        user.schools.length === 1 &&
        renderTeacherSchoolSection()}
      {user.roles.includes("student") && renderClassSection()}
      {user.roles.includes("teacher") && renderContentPreferenceSection()}
      {!user.roles.includes("student") && renderAdditionalInfoSection()}
    </>
  );
};

ProfileDetails.propTypes = propTypes;

export default ProfileDetails;
